import {
  MEMBER_MARKETING_CONTRACT_VERSION,
  type PublicMemberMarketingUnsubscribeRequest,
} from "@billiard/contracts";
import { useMemo, useState } from "react";
import { ApiRequestError } from "./api";
import { unsubscribeMemberMarketing } from "./memberMarketingApi";

const TOKEN = /^[A-Za-z0-9_-]{43}$/u;
const SESSION_KEY = "billiard-os.member-marketing.public-unsubscribe.v1";

function requestFor(token: string): PublicMemberMarketingUnsubscribeRequest {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as PublicMemberMarketingUnsubscribeRequest;
      if (parsed.token === token && parsed.contractVersion === MEMBER_MARKETING_CONTRACT_VERSION) {
        return parsed;
      }
    }
  } catch {
    // A fresh idempotency key is still safe because opt-out itself is monotonic.
  }
  const request = {
    contractVersion: MEMBER_MARKETING_CONTRACT_VERSION,
    token,
    idempotencyKey: `public-marketing-optout-${crypto.randomUUID()}`,
  } satisfies PublicMemberMarketingUnsubscribeRequest;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(request));
  } catch {
    // Keep the same in-memory request for this page lifetime.
  }
  return request;
}

export function MarketingUnsubscribePage(): React.JSX.Element {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") ?? "", []);
  const request = useMemo(() => (TOKEN.test(token) ? requestFor(token) : null), [token]);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [failure, setFailure] = useState("");

  const submit = async (): Promise<void> => {
    if (!request) return;
    setBusy(true);
    setFailure("");
    try {
      const result = await unsubscribeMemberMarketing(request);
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        // The authoritative opt-out result is already confirmed.
      }
      setSuccess(
        `${result.channel === "SMS" ? "短信" : "微信"}营销已退订。尚未发送的同渠道营销任务已取消；收款、订单、预约等必要交易通知不受影响。`,
      );
    } catch (error) {
      setFailure(
        error instanceof ApiRequestError && error.statusCode !== undefined && error.statusCode < 500
          ? error.message
          : "退订结果暂时无法确认。请保留当前页面并再次提交同一请求，系统不会重复扩大影响。",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="commerce-page">
      <section className="commerce-card" aria-label="顾客营销退订">
        <span className="commerce-eyebrow">BILLIARD OS · PRIVACY</span>
        <h1>停止营销消息</h1>
        <p>此页面只停止本渠道的活动、优惠等营销联系。</p>
        <div className="authorization-note">
          <strong>不会影响必要通知</strong>
          <p>收款结果、订单变化、预约处理等与你正在办理业务有关的通知仍会正常发送。</p>
        </div>
        {!request ? (
          <div className="error-band" role="alert">
            退订链接无效或不完整，请使用营销消息中的完整链接。
          </div>
        ) : null}
        {success ? <output className="notice-band">{success}</output> : null}
        {failure ? (
          <div className="error-band" role="alert">
            {failure}
          </div>
        ) : null}
        {!success && request ? (
          <button
            className="button primary"
            type="button"
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? "正在提交…" : "确认停止本渠道营销"}
          </button>
        ) : null}
      </section>
    </main>
  );
}
